import { useState, type ReactNode } from 'react';
import type { DetalleDeProfesional, EstadoDeVerificacion, ProfesionalAdministrado } from '@medchoice/shared';
import { administracion } from '../../api/administracion';
import { ApiError } from '../../api/client';
import { usePedido } from '../../api/usePedido';
import { irA, volver } from '../../router';
import type { ScreenProps } from '../../routes/types';
import {
  AreaTexto, AvisoError, Avatar, Boton, Cargando, Entrada, EstadoVacio, FalloDeCarga, Icono, Insignia, Modal, Pestanas,
  Tarjeta, useAvisos, type TonoInsignia, useTitulo,
} from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { fechaCorta } from '../../ui/format';

const VERIFICACION: Record<EstadoDeVerificacion, { tono: TonoInsignia; texto: string }> = {
  verificado: { tono: 'confirmado', texto: 'Verificado' },
  pendiente: { tono: 'pendiente', texto: 'Sin verificar' },
  rechazado: { tono: 'ausente', texto: 'Rechazado' },
};

const nombre = (p: { nombre: string; apellido: string }) => `${p.nombre} ${p.apellido}`;

function Estados({ p }: { p: ProfesionalAdministrado }) {
  return (
    <>
      <Insignia tono={VERIFICACION[p.estado_verificacion].tono} tamano="chico">{VERIFICACION[p.estado_verificacion].texto}</Insignia>
      {p.estado_cuenta === 'suspendida' && <Insignia tono="cancelado" tamano="chico">Suspendido</Insignia>}
    </>
  );
}

type Pestana = 'pendientes' | 'todos';

export function Profesionales() {
  const pedido = usePedido(() => administracion.profesionales(), []);
  const [pestana, setPestana] = useState<Pestana>('pendientes');
  const [busqueda, setBusqueda] = useState('');
  const todos = pedido.datos ?? [];
  const pendientes = todos.filter((p) => p.estado_verificacion === 'pendiente');
  const texto = busqueda.trim().toLowerCase();
  const lista = (pestana === 'pendientes' ? pendientes : todos).filter((p) => !texto
    || nombre(p).toLowerCase().includes(texto) || p.nro_matricula.toLowerCase().includes(texto)
    || (p.especialidad ?? '').toLowerCase().includes(texto));

  return (
    <SeccionPanel titulo="Profesionales" descripcion="Verificación de matrículas y estado de las cuentas">
      {pedido.estado === 'cargando' && <Cargando />}
      {pedido.estado === 'error' && <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />}
      {pedido.datos && (
        <Tarjeta>
          <Pestanas<Pestana> etiqueta="Profesionales" activa={pestana} alElegir={setPestana} pestanas={[
            { id: 'pendientes', texto: 'Verificación pendiente', cantidad: pendientes.length },
            { id: 'todos', texto: 'Todos', cantidad: todos.length },
          ]} />
          <div className="listado">
            <Entrada aria-label="Buscar profesional" icono="search" maxLength={50} value={busqueda}
              placeholder="Buscar por nombre, matrícula o especialidad" onChange={(e) => setBusqueda(e.target.value)} />
            {lista.length === 0 ? (
              pestana === 'pendientes' && !texto
                ? <EstadoVacio icono="shield-check" titulo="No hay matrículas pendientes" cuerpo="Todas las cuentas nuevas están revisadas." />
                : <EstadoVacio icono="search" titulo="Sin resultados" cuerpo={`Ningún profesional coincide con “${busqueda}”.`}
                  accion={<Boton variante="secundario" onClick={() => setBusqueda('')}>Limpiar la búsqueda</Boton>} />
            ) : lista.map((p) => (
              <button key={p.id_profesional} type="button" className="fila-turno" onClick={() => irA(`/admin/profesional/${p.id_profesional}`)}>
                <Avatar nombre={nombre(p)} tamano={36} decorativo />
                <span className="fila-turno__datos">
                  <strong>{nombre(p)}</strong>
                  <span>{p.especialidad ?? 'Sin especialidad'} · {p.nro_matricula} ({p.jurisdiccion_matricula}) · plan {p.plan ?? '—'}</span>
                </span>
                <Estados p={p} />
                <Icono nombre="chevron-right" tamano={17} />
              </button>
            ))}
          </div>
        </Tarjeta>
      )}
    </SeccionPanel>
  );
}

export function DetalleProfesional({ params }: ScreenProps) {
  const id = Number(params['id']);
  const pedido = usePedido(() => administracion.profesional(id), [id]);
  useTitulo(pedido.datos && nombre(pedido.datos));
  const accionVolver = <Boton tamano="chico" variante="secundario" icono="arrow-left" onClick={() => volver('/admin/profesionales')}>Volver</Boton>;
  if (!pedido.datos) {
    return (
      <SeccionPanel titulo="Profesional" acciones={accionVolver}>
        {pedido.estado === 'cargando'
          ? <Cargando />
          : <FalloDeCarga mensaje={pedido.error ?? 'No encontramos al profesional'} alReintentar={pedido.recargar} />}
      </SeccionPanel>
    );
  }
  return <Detalle p={pedido.datos} volverAtras={accionVolver} alCambiar={pedido.recargar} />;
}

function Detalle({ p, volverAtras, alCambiar }: { p: DetalleDeProfesional; volverAtras: ReactNode; alCambiar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [resolviendo, setResolviendo] = useState<'aprobar' | 'rechazar' | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const suspendido = p.estado_cuenta === 'suspendida';

  const cambiarCuenta = async () => {
    setOcupado(true);
    try {
      await administracion.cambiarEstadoDeCuenta(p.id_profesional, { estado: suspendido ? 'activa' : 'suspendida' });
      mostrarAviso({ tono: 'exito', titulo: suspendido ? 'Cuenta reactivada' : 'Cuenta suspendida',
        cuerpo: suspendido ? 'Vuelve a aparecer en la búsqueda.' : 'Se cerraron sus sesiones y deja de aparecer en la búsqueda.' });
      alCambiar();
    } catch (e) {
      mostrarAviso({ tono: 'error', titulo: 'No se pudo cambiar la cuenta', cuerpo: e instanceof ApiError ? e.message : undefined });
    } finally {
      setOcupado(false);
    }
  };

  return (
    <SeccionPanel titulo={nombre(p)} acciones={volverAtras}
      descripcion={`${p.especialidad ?? 'Sin especialidad'} · alta el ${fechaCorta(p.fecha_alta.slice(0, 10))}`}>
      <div className="dos-columnas">
        <div className="pila">
          <Tarjeta>
            <header className="tarjeta-titulo">
              <h3>Matrícula</h3>
              <div className="chips"><Estados p={p} /></div>
            </header>
            <dl className="datos-modal">
              <div><dt>Número</dt><dd>{p.nro_matricula}</dd></div>
              <div><dt>Jurisdicción</dt><dd>{p.jurisdiccion_matricula}</dd></div>
              <div><dt>Correo</dt><dd>{p.email}</dd></div>
              <div><dt>Teléfono</dt><dd>{p.telefono}</dd></div>
              <div><dt>Plan</dt><dd>{p.plan ?? '—'}</dd></div>
            </dl>
            {p.estado_verificacion !== 'verificado' && (
              <div className="acciones-turno">
                <Boton onClick={() => setResolviendo('aprobar')}>Aprobar matrícula</Boton>
                {p.estado_verificacion === 'pendiente' && (
                  <Boton variante="peligro" onClick={() => setResolviendo('rechazar')}>Rechazar</Boton>
                )}
              </div>
            )}
          </Tarjeta>
          <Tarjeta>
            <h3 className="titulo-tarjeta">Documentación cargada</h3>
            {p.documentos.length === 0 ? <p className="nota">No cargó documentación todavía.</p> : (
              <ul className="lista-bloqueos">
                {p.documentos.map((d) => (
                  <li key={d.id_documento}>
                    <Icono nombre="file-text" tamano={16} />
                    <span>{d.url_archivo.split('/').pop()} · {d.tipo}</span>
                    <span className="nota">{fechaCorta(d.fecha_carga.slice(0, 10))}</span>
                  </li>
                ))}
              </ul>
            )}
          </Tarjeta>
        </div>
        <div className="pila">
          <Tarjeta>
            <h3 className="titulo-tarjeta">Actividad</h3>
            <div className="numeros-paciente">
              <div><strong>{p.turnos_atendidos}</strong><span>turnos atendidos</span></div>
              <div><strong>{p.reclamos}</strong><span>reclamos recibidos</span></div>
              <div><strong>{p.verificaciones.length}</strong><span>revisiones</span></div>
            </div>
            <Boton anchoCompleto variante={suspendido ? 'primario' : 'peligro'} cargando={ocupado} onClick={() => void cambiarCuenta()}>
              {suspendido ? 'Reactivar la cuenta' : 'Suspender la cuenta'}
            </Boton>
          </Tarjeta>
          <Tarjeta>
            <h3 className="titulo-tarjeta">Historial de verificación</h3>
            {p.verificaciones.length === 0 ? <p className="nota">Todavía no se revisó.</p> : (
              <ul className="notas">
                {p.verificaciones.map((v) => (
                  <li key={v.fecha_resolucion}>
                    <span>{fechaCorta(v.fecha_resolucion.slice(0, 10))}</span>
                    {v.estado === 'aprobada' ? 'Aprobada' : 'Rechazada'} por {v.administrador}
                    {v.observaciones ? `: ${v.observaciones}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </Tarjeta>
        </div>
      </div>
      {resolviendo && (
        <ModalVerificacion profesional={p} aprobar={resolviendo === 'aprobar'} alCerrar={() => setResolviendo(null)} alResolver={alCambiar} />
      )}
    </SeccionPanel>
  );
}

function ModalVerificacion({ profesional, aprobar, alCerrar, alResolver }: {
  profesional: DetalleDeProfesional; aprobar: boolean; alCerrar: () => void; alResolver: () => void;
}) {
  const { mostrarAviso } = useAvisos();
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const resolver = async () => {
    setEnviando(true);
    try {
      await administracion.verificar(profesional.id_profesional, { aprobar, observaciones: observaciones.trim() || undefined });
      mostrarAviso({ tono: 'exito', titulo: aprobar ? 'Matrícula aprobada' : 'Matrícula rechazada',
        cuerpo: aprobar ? 'El perfil ya aparece en la búsqueda.' : 'El perfil no aparece en la búsqueda hasta que se corrija.' });
      alCerrar();
      alResolver();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos guardar la verificación. Probá de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal abierto alCerrar={alCerrar} titulo={aprobar ? 'Aprobar matrícula' : 'Rechazar matrícula'} ancho={540}
      pie={<>
        <Boton variante="secundario" onClick={alCerrar}>Cancelar</Boton>
        <Boton variante={aprobar ? 'primario' : 'peligro'} cargando={enviando}
          disabled={!aprobar && observaciones.trim().length < 10} onClick={() => void resolver()}>
          {aprobar ? 'Aprobar' : 'Rechazar'}
        </Boton>
      </>}>
      <AvisoError>{error}</AvisoError>
      <p className="nota">
        {aprobar
          ? `Confirmá que la matrícula ${profesional.nro_matricula} (${profesional.jurisdiccion_matricula}) coincide con la documentación y con el registro oficial.`
          : 'Contale al profesional por qué, así puede corregirlo.'}
      </p>
      <AreaTexto etiqueta={aprobar ? 'Observación (opcional)' : 'Motivo del rechazo'} rows={3} maxLength={500}
        value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
    </Modal>
  );
}
